import { useEffect, useRef, useState } from 'react';
import AdminPage from './AdminPage.jsx';
import LoginPage from './LoginPage.jsx';
import {
  fetchMe,
  redeemEmailLink,
  redeemImpersonation,
  sendVerificationEmail,
  setAccountName,
} from './api.js';
import { ACCOUNT_NAME_MAX } from './config.js';
import { useToast } from './Toaster.jsx';

// Pulled out of the URL as it is read, so a reload does not try to spend a
// credential that has already been spent, and so it stops being visible in the
// address bar of a browser somebody else may be looking at - or in whatever
// keeps a browser history, which for a link that arrived by email is a great
// deal longer-lived than the link itself.
function takeParam(name) {
  const params = new URLSearchParams(window.location.search);
  const value = params.get(name);
  if (!value) return '';

  params.delete(name);
  const query = params.toString();
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
  );
  return value;
}

// Redeemed once per page load rather than once per effect run. React's
// StrictMode mounts twice in development, and both of these links are
// single-use: a second POST would find the credential already spent and report
// a working link as broken. The promises are shared, so both runs wait on the
// same redemption.
//
// Two separate ones because they are two unrelated ways to arrive - a link
// pasted from a site admin, and a link that came by email - and neither should
// be skipped because the other was absent. Both take their value out of the
// URL at the moment they are created, which is what makes them one-shot.
let impersonationRedemption = null;
function redeemImpersonationOnce() {
  if (!impersonationRedemption) {
    const code = takeParam('impersonate');
    impersonationRedemption = code ? redeemImpersonation(code) : Promise.resolve(null);
  }
  return impersonationRedemption;
}

let emailRedemption = null;
function redeemEmailLinkOnce() {
  if (!emailRedemption) {
    const token = takeParam('token');
    emailRedemption = token ? redeemEmailLink(token) : Promise.resolve(null);
  }
  return emailRedemption;
}

// What to say once a link has done its work. Signing in says nothing at all:
// the page changing from the sign-in form to the admin site is the whole
// message, and a toast confirming it would be noise. The other three each
// changed something that is not otherwise visible on the page you land on.
function linkOutcome(result) {
  if (!result) return '';
  switch (result.purpose) {
    case 'signIn':
      return result.created ? 'Welcome to Litbyt — your account is ready.' : '';
    case 'verify':
      return 'Thanks — your email address is confirmed.';
    case 'changeEmail':
      return `Your email address is now ${result.account?.email}.`;
    default:
      return '';
  }
}

// Decides whether the admin site shows the sign-in form or the settings, by
// asking the server who we are rather than trusting anything local - the
// session lives in an HttpOnly cookie that JavaScript cannot read.
export default function AdminRoute() {
  const notify = useToast();
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(true);
  // Whether the link just redeemed is what brought this account into
  // existence, which is the only moment there is to ask a new person their
  // name. Sign-in and sign-up are one endpoint that cannot tell them apart
  // until it has run - see docs/email-auth.md - so there is no form to put the
  // field on beforehand, and this is the far side of it.
  const [naming, setNaming] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function resolve() {
      // Both kinds of link have to be redeemed before we ask who we are,
      // because redeeming one is what decides the answer.
      try {
        await redeemImpersonationOnce();
      } catch (error) {
        if (!cancelled) {
          notify(`That impersonation link did not work: ${error.message}`, { tone: 'error' });
        }
      }

      try {
        const result = await redeemEmailLinkOnce();
        const outcome = linkOutcome(result);
        if (outcome && !cancelled) notify(outcome);
        // Asked once, here, and never again: an account that got past this -
        // by reloading the page, or because it predates the field - is left
        // alone and offered the same field under Account instead. Blocking a
        // live account out of its own settings over a name would be a worse
        // thing than a link that unfurls without one.
        if (result?.created && !cancelled) setNaming(true);
      } catch (error) {
        if (!cancelled) {
          notify(`That link did not work: ${error.message}`, { tone: 'error' });
        }
      }

      try {
        const data = await fetchMe();
        if (!cancelled) setMe(data);
      } catch {
        if (!cancelled) setMe(null);
      } finally {
        if (!cancelled) setChecking(false);
      }
    }

    resolve();
    return () => {
      cancelled = true;
    };
  }, [notify]);

  if (checking) return <p className="status-message">Checking sign-in…</p>;
  if (me?.account && naming) {
    return (
      <NamePage
        onNamed={async () => {
          setNaming(false);
          try {
            setMe(await fetchMe());
          } catch {
            // The name is written; who we are is what could not be re-read.
            // Leaving `me` as it was shows the site with a stale name on it,
            // which is the smaller of the two wrongs.
          }
        }}
      />
    );
  }
  if (!me?.account) {
    // Re-asking rather than keeping what sign-in returned: the answer carries
    // the role and the nav is built from it, and only the server is entitled
    // to say what it is.
    return <LoginPage onSignedIn={() => fetchMe().then(setMe).catch(() => setMe(null))} />;
  }

  return (
    <>
      {me.impersonatedBy && <ImpersonationBanner me={me} />}
      {/* Only ever one of the two. An impersonated session is somebody else's
          address to confirm, not this person's, and stacking two sticky
          banners would push the page down for a nudge that is not even theirs
          to act on. */}
      {!me.impersonatedBy && !me.emailVerified && (
        <UnverifiedBanner email={me.account.email} onVerified={() => fetchMe().then(setMe).catch(() => {})} />
      )}
      <AdminPage
        me={me}
        onSignedOut={() => setMe(null)}
        // The address is in the sidebar, so a change to it has to reach the
        // shell rather than staying inside the panel that made it.
        onAccountChanged={() => fetchMe().then(setMe).catch(() => {})}
      />
    </>
  );
}

// The one thing a new account is asked for, on the far side of the link that
// created it.
//
// It is a gate rather than a nudge, which is the opposite of what
// UnverifiedBanner below is, and the difference is who is standing there. That
// banner meets accounts that have been running for months and were never asked
// to confirm an address; this meets somebody who signed up nine seconds ago and
// is expecting to be asked something. There is nothing behind it to be kept
// out of yet.
function NamePage({ onNamed }) {
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      await setAccountName(name);
      onNamed();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  return (
    <main className="page-shell login-page">
      <form className="panel login-panel" onSubmit={handleSubmit}>
        <p className="eyebrow">Litbyt</p>
        <h1>What should we call you?</h1>
        <p className="panel-hint">
          It goes on the link you send people — a shelf shared with a friend arrives
          as “Claire’s Litbyt” rather than as a web address. You can change it later
          under Account.
        </p>

        <label className="field">
          <span>Your name</span>
          <input
            type="text"
            autoComplete="given-name"
            autoFocus
            maxLength={ACCOUNT_NAME_MAX}
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
          />
        </label>

        <button type="submit" className="save-button" disabled={busy || !name.trim()}>
          {busy ? 'Saving…' : 'Continue'}
        </button>

        {error && <p className="login-error">{error}</p>}
      </form>
    </main>
  );
}

// A banner and the sidebar are both stuck to the top of the window, so the
// sidebar has to start below whichever banner is showing rather than under it.
// Measured rather than assumed, because a banner wraps to two lines on a
// narrow screen and any number written here would be wrong on one of them.
//
// Shared by both banners, and only ever one of them is mounted - see where
// they are rendered. Two at once would each write this and the second would
// win, leaving the sidebar tucked under the taller one.
function useBannerHeight() {
  const banner = useRef(null);

  useEffect(() => {
    const node = banner.current;
    if (!node) return undefined;

    const measure = () => {
      document.documentElement.style.setProperty('--banner-height', `${node.offsetHeight}px`);
    };
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--banner-height');
    };
  }, []);

  return banner;
}

// A nudge, not a gate.
//
// Every account that existed before addresses were ever confirmed is in this
// state, and those are live accounts with displays on the wall: blocking them
// until they act would break a working service to enforce a thing nobody had
// been asked for. So this bar says what is unconfirmed and offers the one
// click that settles it, and nothing on the site is refused meanwhile.
//
// It is quiet where the impersonation banner is loud, and that is the right
// order of alarm: one of them means the account you are changing is not yours.
function UnverifiedBanner({ email, onVerified }) {
  const notify = useToast();
  const banner = useBannerHeight();
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleSend() {
    setBusy(true);
    try {
      const result = await sendVerificationEmail();
      // Already confirmed - most likely in another tab, or by following a link
      // sent earlier. Nothing was sent, and the banner should simply go.
      if (result.verified) {
        onVerified();
        return;
      }
      setSent(true);
    } catch (error) {
      notify(`Could not send that: ${error.message}`, { tone: 'error' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="verify-banner" ref={banner}>
      <span>
        {sent ? (
          <>
            Link sent to <strong>{email}</strong>. Open it to confirm this address.
          </>
        ) : (
          <>
            <strong>{email}</strong> has not been confirmed yet. Confirming it is how we know
            we can reach you — and how you sign in without a password.
          </>
        )}
      </span>
      {!sent && (
        <button type="button" className="admin-link ghost" onClick={handleSend} disabled={busy}>
          {busy ? 'Sending…' : 'Send me a link'}
        </button>
      )}
    </div>
  );
}

// Loud on purpose, and on every page. Someone acting as another person needs
// to be unable to forget it - every change made here is made to a real
// account, and looks to its owner exactly like something they did themselves.
function ImpersonationBanner({ me }) {
  const banner = useBannerHeight();

  return (
    <div className="impersonation-banner" role="alert" ref={banner}>
      <strong>Impersonating {me.account.email}</strong>
      <span>
        Signed in through a link created by {me.impersonatedBy}. Anything you change here
        changes their account. Sign out to end it.
      </span>
    </div>
  );
}
